//testing if we get export in excel features July 03, 2026.
import React, { useEffect, Suspense, useState } from "react";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import lazyWithRetry from "./utils/lazyWithRetry";
import { Provider } from "react-redux";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import store from "./store/store";
// --- Static imports (always needed on every page load) ---
import { clearError, checkSession } from "./slice/authSlice";
import { axiosClientSignIn } from "./axios/api";
import {
  CssBaseline,
  ListItemIcon,
  Box,
  CircularProgress,
} from "@mui/material"
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { StorageRounded } from "@mui/icons-material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar";
import Sidebar from "./Sidebar";
import { useLocation } from "react-router-dom";
import { createTheme, ThemeProvider, responsiveFontSizes } from "@mui/material/styles";
import axiosClient from "./axios/api";
import CancelIcon from "@mui/icons-material/Cancel";
import TaskIcon from "@mui/icons-material/Task";
import { connectSocket } from "./socket/socket";
import Loader from './Loader/Loader.jsx'
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// --- Layout shells (static — they wrap lazy children) ---
import EmailMarketingLayout from './Email-Marketing/EmailMarketingLayout';
import VerificationLayout from './Email-Marketing/verification/VerificationLayout.jsx';
import WhatsAppLayout from './WhatsApp-Marketing/WhatsAppLayout.jsx';
import Dashboard from "./dashboard";

// --- Lazy-loaded page components (code-split into separate chunks) ---

// Core Pages
const SignIn = lazyWithRetry(() => import('./page/SignIn'));
const SignUp = lazyWithRetry(() => import('./page/SignUp'));
const Leads = lazyWithRetry(() => import('./page/Leads'));
const Contact = lazyWithRetry(() => import('./page/Contact'));
const Account = lazyWithRetry(() => import('./page/Account'));
const Order = lazyWithRetry(() => import('./orders/Order'));
const Approve = lazyWithRetry(() => import('./page/Approve'));
const ApproveRegret = lazyWithRetry(() => import('./page/ApproveRegret.jsx'));
const AboutMee = lazyWithRetry(() => import('./page/AboutMe'));
const ResetApproval = lazyWithRetry(() => import('./page/ResetApproval'));
const NotFound = lazyWithRetry(() => import('./Notfound/Notfound'));
const LoginPage = lazyWithRetry(() => import('./Pages/LoginPage'));

// Dashboard & Entry
const DashboardCards = lazyWithRetry(() => import('./dashboardCard'));
const EntryPoint = lazyWithRetry(() => import('./entrypoint/EntryPoint'));

// CRM / Company
const CurrentCustomerPage = lazyWithRetry(() => import('./CurrentCustomer/CurrentCustomerPage'));
const CompanyDetails = lazyWithRetry(() => import('./Pages/CompanyDetails'));
const TargetCompanyDetails = lazyWithRetry(() => import('./Pages/TargetCompanyDetails'));
const DatabasePage = lazyWithRetry(() => import('./components/DatabasePage'));
const TargetCustomer = lazyWithRetry(() => import('./Pages/TargetCustomer'));
const DeletedCompanyDetails = lazyWithRetry(() => import('./Pages/DeletedCompanyDetails'));
const CustomizedHook = lazyWithRetry(() => import('./Pages/CustomizedHook'));
const CardComponent = lazyWithRetry(() => import('./Pages/CardComponent'));
const Statistics = lazyWithRetry(() => import('./Pages/Statistics'));
const StatisticsDetail = lazyWithRetry(() => import('./Pages/StatisticsDetail'));
const NotInterested = lazyWithRetry(() => import('./Pages/NotInterested.jsx'));

// Vendor
const VendorDatabase = lazyWithRetry(() => import('./vendor/VendorDatabase'));
const DeletedVendor = lazyWithRetry(() => import('./vendor/DeletedVendor'));
const VendorDetails = lazyWithRetry(() => import('./vendor/VendorDetails'));
const AddVendorSpecs = lazyWithRetry(() => import('./vendor/AddVendorSpecs'));
const InquiryMatchSpecs = lazyWithRetry(() => import('./vendor/InquiryMatchSpecs'));
const PurchasePage = lazyWithRetry(() => import('./components/PurchasePage'));
const PurchaseID = lazyWithRetry(() => import('./components/PurchaseID'));
const Compitator = lazyWithRetry(() => import('./compitator/Compitator.jsx'));
const VendorCompetitor = lazyWithRetry(() => import('./compitator/VendorCompetitor.jsx'));

// RFQ / Estimation
const Inquiryhandler = lazyWithRetry(() => import('./components/Inquiryhandler.jsx'));
const EstimationForm = lazyWithRetry(() => import('./components/EstimationForm'));
const EstimationHeader = lazyWithRetry(() => import('./components/EstimationHeader'));
const Header = lazyWithRetry(() => import('./components/Header'));
const FollowUp = lazyWithRetry(() => import('./FollowUp/components/FollowUpBuying.jsx'));
const FollowupOrder = lazyWithRetry(() => import('./FollowupOrder/FollowupOrder'));
const FollowUpBiddingBuying = lazyWithRetry(() => import('./FollowUp/components/FollowUpBiddingBudgetary.jsx'));
const TripPlannerDashboard = lazyWithRetry(() => import('./Trip_Planner/TripPlannerDashboard.jsx'));
const CountryPlanner = lazyWithRetry(() => import('./Trip_Planner/CountryPlanner.jsx'));
const TripPlannerSettings = lazyWithRetry(() => import('./Trip_Planner/TripPlannerSettings.jsx'));
const ColdCalling = lazyWithRetry(() => import('./components/ColdCalling'));
const CurrentCustomerColdCalling = lazyWithRetry(() => import('./components/CurrentCustomerColdCalling.jsx'));
const TargetCustomerColdCalling = lazyWithRetry(() => import('./components/TargetCustomerColdCalling.jsx'));
const QuotationHeader = lazyWithRetry(() => import('./components/QuotationHeader'));
const QuotationForm = lazyWithRetry(() => import('./components/QuotationForm'));
const QuotationPDF = lazyWithRetry(() => import('./pdf/quotationpdf_test.jsx'));
const EmailEstimationForm = lazyWithRetry(() => import('./EmailEstimation/EmailEstimationForm'));
const EmailEstimationFormOthers = lazyWithRetry(() => import('./EmailEstimation/EmailEstimationFormOthers'));
const SolDatabase = lazyWithRetry(() => import('./components/SolDatabase.jsx'));

// Material Code
const MaterialCode = lazyWithRetry(() => import('./MaterialCode/MaterialCode'));
const MaterialCodeGenerator = lazyWithRetry(() => import('./MaterialCode/MaterialCodeGenerator'));
const MaterialCodeTable = lazyWithRetry(() => import('./Pages/MaterialCodeTable'));

// Chat
const ChatApp = lazyWithRetry(() => import('./chat/Chatcomponet/ChatApp'));
const Smallmain = lazyWithRetry(() => import('./chat/Chatcomponet/Small/SmallMain'));
const Thread = lazyWithRetry(() => import('./chat/Chatcomponet/Threadlist'));
const ThreadWindow = lazyWithRetry(() => import('./chat/Chatcomponet/Thread'));

// Tasks
const Kanban = lazyWithRetry(() => import('./task/Taskkanban.jsx'));
const TaskDisscutionManager = lazyWithRetry(() => import('./task/TaskDisscutionManager.jsx'));
const ListAllTaskDiscussion = lazyWithRetry(() => import('./task/ListAllTaskDiscussion.jsx'));
const Cleanhelp = lazyWithRetry(() => import('./task/cleanmaintask.jsx'));
const TestTaskScheduler = lazyWithRetry(() => import('./Taskscheduler/testmainschedular.jsx'));
const TaskManager = lazyWithRetry(() => import('./Pages/TaskManager'));
const HelpTicketUI = lazyWithRetry(() => import('./help/HelpMain.jsx'));

// HR
const EmployeeDirectory = lazyWithRetry(() => import('./hr/Employee/EmployeeDirectory'));
const EmployeeProfile = lazyWithRetry(() => import('./hr/Employee/EmployeeProfile'));
const DesignationList = lazyWithRetry(() => import('./hr/Designation/DesignationList'));
const DesignationPage = lazyWithRetry(() => import('./hr/Designation/DesignationPage'));
const PayrollDashboard = lazyWithRetry(() => import('./hr/Payroll/PayrollDashboard'));
const AttendanceDashboard = lazyWithRetry(() => import('./hr/Attendance/AttendanceDashboard'));
const LeaveManagement = lazyWithRetry(() => import('./hr/Leave/LeaveManagement'));
const HrDashboard = lazyWithRetry(() => import('./hr/HRDashboard.jsx'));
const AllowanceList = lazyWithRetry(() => import('./hr/Payroll/Allowances/AllowanceList'));
const AllowanceView = lazyWithRetry(() => import('./hr/Payroll/Allowances/AllowanceList.jsx'));
const DeductionList = lazyWithRetry(() => import('./hr/Payroll/Deductions/DeductionList'));
const LoanList = lazyWithRetry(() => import('./hr/Payroll/LoanAdvances/LoanList'));
const ReimbursementList = lazyWithRetry(() => import('./hr/Payroll/Reimbursement/ReimbursementList'));
const PayslipList = lazyWithRetry(() => import('./hr/Payroll/Payslip/PayslipList'));
const AIAutomationDashboard = lazyWithRetry(() => import('./hr/components/AIAutomationDashboard'));
const RecruitmentDashboard = lazyWithRetry(() => import('./hr/Recruitment/RecruitmentDashboard'));
const JobPostingList = lazyWithRetry(() => import('./hr/Recruitment/JobPostings/JobPostingList'));
const CandidateList = lazyWithRetry(() => import('./hr/Recruitment/Candidates/CandidateList'));
const PipelineList = lazyWithRetry(() => import('./hr/Recruitment/RecruitmentPipeline/PipelineList'));
const InterviewList = lazyWithRetry(() => import('./hr/Recruitment/Interviews/InterviewList'));
const CallDetails = lazyWithRetry(() => import('./hr/Recruitment/CallDetails/CallDetails'));
const CallReport = lazyWithRetry(() => import('./hr/Recruitment/CallReport/CallReport'));
const InterviewView = lazyWithRetry(() => import('./hr/Recruitment/Interviews/InterviewView'));
const CandidateManagement = lazyWithRetry(() => import('./hr/Recruitment/Candidates/CandidateManagement'));
const InterviewScheduler = lazyWithRetry(() => import('./hr/Recruitment/Interviews/InterviewScheduler'));
const CandidateView = lazyWithRetry(() => import('./hr/Recruitment/Candidates/CandidateView'));

// Misc
const Aboutus = lazyWithRetry(() => import('./about_us/aboutus.jsx'));
const TrainingCourseList = lazyWithRetry(() => import('./about_us/Training/index.js').then(m => ({ default: m.CourseList })));
const TrainingCourseDetail = lazyWithRetry(() => import('./about_us/Training/index.js').then(m => ({ default: m.CourseDetail })));
const DocumentManagement = lazyWithRetry(() => import('./about/DocumentManagement/DocumentManagement'));
const ClientTable = lazyWithRetry(() => import('./ClientVendorRegistration/MainWindow.jsx'));
const Domain = lazyWithRetry(() => import('./DominExpire/DomainExpire.jsx'));
const AIPromotedTest = lazyWithRetry(() => import('./AI_promot/AI_test'));
const Promote = lazyWithRetry(() => import('./AI_promot/Promot'));
const UserList = lazyWithRetry(() => import('./UserList/UserList.jsx'));
const UserInformation = lazyWithRetry(() => import('./UserInformation/UserInfromation.jsx'));
const AirVendors = lazyWithRetry(() => import('./Logistics/Air/AirVendors'));
const SeaVendors = lazyWithRetry(() => import('./Logistics/Sea/SeaVendors'));
const RoadVendors = lazyWithRetry(() => import('./Logistics/Road/RoadVendors'));
const Test = lazyWithRetry(() => import('./Pages/Test'));

// Email Marketing (already lazy)
const EmailDashboard = lazyWithRetry(() => import('./Email-Marketing/pages/Dashboard'));
const CampaignsPage = lazyWithRetry(() => import('./Email-Marketing/campaigns/CampaignsPage'));
const CampaignProfilePage = lazyWithRetry(() => import('./Email-Marketing/campaigns/profile/CampaignProfilePage.jsx'));
const TemplatesPage = lazyWithRetry(() => import('./Email-Marketing/template/TemplatesPage'));
const SubscribersPage = lazyWithRetry(() => import('./Email-Marketing/subscribers/SubscribersPage'));

// Email Verification (already lazy)
const VerifyPage = lazyWithRetry(() => import('./Email-Marketing/verification/pages/VerifyPage.jsx'));
const BatchPage = lazyWithRetry(() => import('./Email-Marketing/verification/pages/BatchPage.jsx'));
const BatchDetailPage = lazyWithRetry(() => import('./Email-Marketing/verification/pages/BatchDetailPage.jsx'));
const HistoryPage = lazyWithRetry(() => import('./Email-Marketing/verification/pages/HistoryPage.jsx'));
const AnalyticsPage = lazyWithRetry(() => import('./Email-Marketing/verification/pages/AnalyticsPage.jsx'));

// WhatsApp CRM (already lazy)
const WhatsAppDashboard = lazyWithRetry(() => import('./WhatsApp-Marketing/pages/Dashboard.jsx'));
const WhatsAppLiveInbox = lazyWithRetry(() => import('./WhatsApp-Marketing/pages/Inbox/LiveInbox.jsx'));
const WhatsAppCampaigns = lazyWithRetry(() => import('./WhatsApp-Marketing/pages/Campaigns.jsx'));
const WhatsAppTemplates = lazyWithRetry(() => import('./WhatsApp-Marketing/pages/Templates.jsx'));
const WhatsAppContacts = lazyWithRetry(() => import('./WhatsApp-Marketing/pages/Contacts.jsx'));

// Sales Command Center (lazy)
const SalesCommandCenterLayout = lazyWithRetry(() => import('./SalesCommandCenter/SalesCommandCenterLayout'));
const SCCCallingQueue = lazyWithRetry(() => import('./SalesCommandCenter/pages/CallingQueue'));
const SCCPipelineBoard = lazyWithRetry(() => import('./SalesCommandCenter/pages/PipelineBoard'));
const SCCCallHistory = lazyWithRetry(() => import('./SalesCommandCenter/pages/CallHistory'));
const SCCHome = lazyWithRetry(() => import('./SalesCommandCenter/pages/CommandCenterHome'));
const SCCDayReview = lazyWithRetry(() => import('./SalesCommandCenter/pages/DayReview'));
const SCCIntelligence = lazyWithRetry(() => import('./SalesCommandCenter/pages/IntelligenceInsights'));
const SCCTeamAccess = lazyWithRetry(() => import('./SalesCommandCenter/pages/TeamAccess'));
let theme = createTheme({
  typography: {
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    h5: { fontSize: "clamp(1.1rem, 0.9rem + 0.6vw, 1.5rem)" },
    h6: { fontSize: "clamp(1rem,   0.85rem + 0.4vw, 1.25rem)" },
    subtitle1: { fontSize: "clamp(0.85rem, 0.75rem + 0.3vw, 1rem)" },
    subtitle2: { fontSize: "clamp(0.78rem, 0.7rem + 0.25vw, 0.875rem)" },
    body1: { fontSize: "clamp(0.8rem,  0.72rem + 0.25vw, 0.9375rem)" },
    body2: { fontSize: "clamp(0.75rem, 0.68rem + 0.2vw,  0.875rem)" },
    caption: { fontSize: "clamp(0.65rem, 0.6rem  + 0.15vw, 0.75rem)" },
  },
  components: {
    MuiModal: {
      defaultProps: {
        disableEnforceFocus: true,
        disableAutoFocus: true,
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: 'clamp(6px, 0.5rem + 0.3vw, 10px) clamp(8px, 0.5rem + 0.5vw, 16px)',
          borderColor: '#f1f5f9',
          fontSize: 'clamp(0.7rem, 0.6rem + 0.25vw, 0.875rem)',
        },
        head: {
          fontWeight: 600,
          color: 'inherit',
          textTransform: 'uppercase',
          fontSize: 'clamp(0.6rem, 0.52rem + 0.2vw, 0.75rem)',
          letterSpacing: '0.05em',
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 600,
          fontSize: 'clamp(0.6rem, 0.55rem + 0.15vw, 0.75rem)',
        }
      }
    }
  }
});

theme = responsiveFontSizes(theme);
// const ProtectedRoute = ({ element }) => {
//   const token = useSelector((state) => state.auth.sessionChecked);
//   console.log("ProtectedRoute: Token exists?", Boolean(token)); // Debugging with boolean
//   return token ? element : <Navigate to="/signin" />;
// }; 


// const ProtectedRoute = ({ element }) => {
//   const sessionChecked = useSelector((state) => state.auth.sessionChecked);
//   const dispatch = useDispatch();
//   // useRealTimeSessionCheck(); 
//   useEffect(() => {
//     console.log("useeffect is running");
//     dispatch(checkSession());
//   }, [dispatch]);
//   if (sessionChecked === null) {
//     return <div>Loading...</div>; // Show a loading screen while checking session
//   }

//   return sessionChecked ? element : <Navigate to="/signin" />;
// };
// const ProtectedRoute = ({ element, pageKey }) => {
//   const sessionChecked = useSelector((state) => state.auth.sessionChecked);
//   const userId = useSelector((state) => state.auth.user?.id);
//   const dispatch = useDispatch();

//   useEffect(() => {
//     dispatch(checkSession());
//   }, [dispatch]);

//   const { loading: pagesLoading, pages } = useUserPagePermissions(pageKey ? userId : null);
//     console.log(pages)

//   if (sessionChecked === null) {
//     return <Loader text="Checking session..." />;
//   }

//   if (!sessionChecked) {
//     return <Navigate to="/signin" />;
//   }

//   if (pageKey) {
//     if (pagesLoading) {
//       return <Loader text="Loading permissions..." />;
//     }
//     if (!pages.includes(pageKey)) {
//       return <Navigate to="/" replace />;
//     }
//   }

//   return element;
// };
// const ProtectedRoute = ({ element, pageKey }) => {
//   const sessionChecked = useSelector((state) => state.auth.sessionChecked);
//   const userId = useSelector((state) => state.auth.user?.id);
//   const dispatch = useDispatch();
//   const [permissionChecked, setPermissionChecked] = useState(false);
//   const [hasPermission, setHasPermission] = useState(false);

//   useEffect(() => {
//     dispatch(checkSession());
//   }, [dispatch]);

//   useEffect(() => {
//     if (pageKey && userId && sessionChecked) {
//       checkPagePermission();
//     } else if (!pageKey && sessionChecked) {
//       setPermissionChecked(true);
//       setHasPermission(true);
//     }
//   }, [pageKey, userId, sessionChecked]);

//   const checkPagePermission = async () => {
//     try {
//       const response = await axiosClientSignIn.get(`/listroute/getpages/${userId}`);
//       const userPages = response.data.pages.map(page => 
//         page.name.toLowerCase().replace(/[^a-z0-9]/g, '')
//       );
//       const normalizedPageKey = pageKey.toLowerCase().replace(/[^a-z0-9]/g, '');

//       setHasPermission(userPages.includes(normalizedPageKey));
//     } catch (error) {
//       console.error("Error checking page permissions:", error);
//       setHasPermission(false);
//     } finally {
//       setPermissionChecked(true);
//     }
//   };

//   if (sessionChecked === null) {
//     return <Loader text="Checking session..." />;
//   }

//   if (!sessionChecked) {
//     return <Navigate to="/signin" />;
//   }

//   if (pageKey) {
//     if (!permissionChecked) {
//       return <Loader text="Checking permissions..." />;
//     }
//     if (!hasPermission) {
//       return <Navigate to="/" replace />;
//     }
//   }

//   return element;
// };

const ProtectedRoute = ({ element }) => {
  const sessionChecked = useSelector((state) => state.auth.sessionChecked);
  const userId = useSelector((state) => state.auth.user?.id);
  const dispatch = useDispatch();
  const [permissionChecked, setPermissionChecked] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const location = useLocation();

  // Extract page name from path
  const getPageKeyFromPath = (pathname) => {
    // Remove leading slash and get the first part
    const path = pathname.replace(/^\//, '').split('/')[0];

    // Handle root path "/" - default to "dashboard"
    if (path === '') {
      // console.log("🏠 Root path detected, defaulting to 'dashboard'");
      return 'dashboard';
    }

    // console.log("🔗 Path to Page Key Conversion:");
    // console.log("   Input Path:", pathname);
    // console.log("   Extracted Page Key:", path);
    return path;
  };

  const pageKey = getPageKeyFromPath(location.pathname);

  // console.log("═══════════════════════════════════════");
  // console.log("🔐 AUTO PROTECTED ROUTE DEBUG");
  // console.log("═══════════════════════════════════════");
  // console.log("📍 CURRENT PATH:", location.pathname);
  // console.log("🔑 AUTO PAGE KEY:", pageKey);
  // console.log("👤 USER ID:", userId);
  // console.log("✅ SESSION CHECKED:", sessionChecked);
  // console.log("───────────────────────────────────────");

  useEffect(() => {
    // console.log("🔄 Dispatching checkSession...");
    dispatch(checkSession());
  }, [dispatch]);

  useEffect(() => {
    // console.log("🔄 Checking auto-permission conditions...");

    if (userId && sessionChecked) {
      console.log("✅ User and session ready - checking page permissions");
      checkPagePermission();
    } else {
      console.log("⏳ Waiting for conditions:", {
        hasUserId: !!userId,
        hasSession: !!sessionChecked
      });
    }
  }, [userId, sessionChecked]);

  const checkPagePermission = async () => {
    // console.log("🚀 STARTING AUTO PAGE PERMISSION CHECK");
    // console.log(`📡 Making API call: /listroute/getpages/${userId}`);

    try {
      const response = await axiosClientSignIn.get(`/listroute/getpages/${userId}`);

      // console.log("✅ API RESPONSE SUCCESS");

      // Get user's available pages
      const userPages = response.data.pages.map(page => {
        const normalized = page.name.toLowerCase().replace(/[^a-z0-9]/g, '');
        return {
          original: page.name,
          normalized: normalized
        };
      });

      // console.log("📋 USER'S AVAILABLE PAGES:");
      userPages.forEach((page, index) => {
        // console.log(`   ${index + 1}. "${page.original}" → "${page.normalized}"`);
      });

      // Normalize the current path to match page names
      const normalizedPageKey = pageKey.toLowerCase().replace(/[^a-z0-9]/g, '');

      // console.log("🔍 AUTO PAGE COMPARISON:");
      // console.log("   Path:", location.pathname);
      // console.log("   Page Key:", pageKey);
      // console.log("   Normalized:", normalizedPageKey);

      // Check if user has access to this page
      const availableNormalizedPages = userPages.map(p => p.normalized);
      const pageExists = availableNormalizedPages.includes(normalizedPageKey);

      console.log("🎯 AUTO MATCH RESULT:");
      if (pageExists) {
        console.log("   ✅ SUCCESS: User has access to this page!");
      } else {
        //   console.log("   ❌ FAILED: User does NOT have access to this page");
        //   console.log("   🔍 Looking for:", normalizedPageKey);
        //   console.log("   📂 Available:", availableNormalizedPages);
      }

      setHasPermission(pageExists);
      setPermissionChecked(true);

    } catch (error) {
      console.error("💥 API ERROR:", error);
      setHasPermission(false);
      setPermissionChecked(true);
    }
  };

  // Session checking
  if (sessionChecked === null) {
    // console.log("⏳ Checking session...");
    return <Loader text="Dashboard is loading ..." />;
  }

  if (!sessionChecked) {
    console.log("🚫 No session - redirecting to signin");
    return <Navigate to="/signin" />;
  }

  // Permission checking
  //   if (!permissionChecked) {
  //    console.log("⏳ Checking page permissions...");
  //     return <Loader text="Checking permissions..." />;
  //   }

  // if (!hasPermission) {
  //  console.log(`❌ No permission for ${location.pathname} - redirecting to /`);
  //      return <Navigate to="/notfound" replace />;
  //    }

  // console.log("🎉 ACCESS GRANTED - Rendering:", element?.type?.name);
  return element;
};
const PreventAuthAccess = ({ element }) => {
  console.log("prventauthacces")
  const dispatch = useDispatch();
  // useRealTimeSessionCheck(); 
  useEffect(() => {
    console.log("useeffect is running");
    dispatch(checkSession());
  }, [dispatch]);
  const sessionChecked = useSelector((state) => state.auth.sessionChecked);
  console.log(sessionChecked, 'prventauthacces')
  if (sessionChecked === null) {
    console.log("i am null")
    return <Loader text="Dashboard is loading ..." />; // Wait until session check is done
  }
  console.log(element)
  return sessionChecked ? <Navigate to="/" /> : element;
};

let cachedUserPages = null;
let cachedUserPagesUserId = null;

const useUserPagePermissions = (userId) => {
  const [state, setState] = useState({
    loading: true,
    pages: [],
  });

  useEffect(() => {
    let isMounted = true;

    if (!userId) {
      setState({ loading: false, pages: [] });
      return () => {
        isMounted = false;
      };
    }

    if (cachedUserPages && cachedUserPagesUserId === userId) {
      setState({ loading: false, pages: cachedUserPages });
      return () => {
        isMounted = false;
      };
    }

    setState((prev) => ({ ...prev, loading: true }));
    const controller = new AbortController();

    axiosClientSignIn
      .get(`/listroute/getpages/${userId}`, { signal: controller.signal })
      .then((response) => {
        if (!isMounted) return;
        const names = response.data?.pages?.map((page) => page.name) || [];
        cachedUserPages = names;
        cachedUserPagesUserId = userId;
        setState({ loading: false, pages: names });
      })
      .catch((error) => {
        if (!isMounted || controller.signal.aborted) return;
        console.error("Failed to load user pages:", error);
        cachedUserPages = [];
        cachedUserPagesUserId = userId;
        setState({ loading: false, pages: [] });
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [userId]);

  return state;
};
const AdminRoute = ({ element }) => {
  const user = useSelector((state) => state.auth.sessionChecked) || JSON.parse(localStorage.getItem("user"));
  // console.log("AdminRoute: User exists?", Boolean(user));
  // console.log("AdminRoute: User is Admin?", user?.role === "admin");

  return user?.role === "admin" ? element : <Navigate to="/" />;
};

const SuperAdminRoute = ({ element }) => {
  const user = useSelector((state) => state.auth.user) || JSON.parse(localStorage.getItem("user") || "null");
  return user?.role === "SUPER_ADMIN" ? element : <Navigate to="/" replace />;
};

const SignUpRedirect = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);

  React.useEffect(() => {
    if (token) {
      navigate("/signin");
      dispatch(clearError());
    }
  }, [token, navigate, dispatch]);
  return <SignUp />;
};

const App = () => {

  const [openMaterial, setOpenMaterial] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [notificationDisplay, setNotificationDisplay] = useState(false);
  const [notificationData, setNotificationData] = useState([]);
  const [isSocketReady, setIsSocketReady] = useState(false);
  const location = useLocation();  // ✅ Capture the current location
  console.log("Current Route---------------------:", location.pathname);

  const dispatch = useDispatch();
  // useRealTimeSessionCheck(); 
  // useEffect(() => {
  //   const run = async () => {
  //     const result = await dispatch(checkSession());

  //     if (checkSession.rejected.match(result)) {
  //       console.warn("❌ Invalid session");

  //     }

  //     const socket = connectSocket("from"); // connectSocket sets socket
  //     if (socket.connected) {
  //       setIsSocketReady(true);
  //     } else {
  //       socket.on("connect", () => {
  //         setIsSocketReady(true);
  //       });
  //     }
  //   };

  //   run();
  // }, []);


  useEffect(() => {
    const run = async () => {
      const result = await dispatch(checkSession());

      if (checkSession.rejected.match(result)) {
        console.warn("❌ Invalid session — skipping socket connection");
      } else if (checkSession.fulfilled.match(result)) {
        console.log("✅ Session valid — connecting socket...");

        const socket = connectSocket("from appjsx");

        if (socket.connected) {
          setIsSocketReady(true);
        } else {
          socket.on("connect", () => {
            if (socket.connected) {
              setIsSocketReady(true);
            }

          });
        }
      } else {
        console.warn("⚠️ Unknown session check result:", result);
      }
    };

    run();
  }, [dispatch]);


  const token = useSelector((state) => state.auth.sessionChecked);
  console.log(token, isSocketReady)
  if (token === undefined && !isSocketReady) {
    return <Loader text="Dashboard is loading ..." />;
  }


  const getCurrentISTTime = () => {
    const date = new Date();
    const options = {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    };
    return date.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }).replace(',', ''); // Format the date and remove the comma
  };

  const handleClick = () => {
    setOpen(!open);
  };

  const handleClickMaterial = () => {
    setOpenMaterial(!openMaterial);
  };

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const handleClearAll = async () => {
    const response = axiosClient.get("/api/notificationclearall");
    if (response.status === 200) {
      fetchNotificationData();
    }
  };

  const handleClearNotification = async (id) => {
    const response = axiosClient.post(`/api/notificationclear/${id}`, { id });
    if (response.status === 200) {
      fetchNotificationData();
    }
  };

  const fetchNotificationLiveData = () => {
    console.log(`[${getCurrentISTTime()}] fetchNotificationLiveData function started.`); // Log when the function starts

    const intervalId = setInterval(async () => {
      console.log(`[${getCurrentISTTime()}] Fetching notification data...`); // Log when fetching data
      try {
        await fetchNotificationData();
        console.log(`[${getCurrentISTTime()}] Notification data fetched successfully.`); // Log on successful fetch
      } catch (error) {
        console.error(`[${getCurrentISTTime()}] Error fetching notification details:`, error);
      }
    }, 1000); // Fetch every 1 second

    // Return a function to stop the interval if needed
    return () => {
      console.log(`[${getCurrentISTTime()}] Clearing interval.`); // Log when clearing the interval
      clearInterval(intervalId);
    };
  };

  const fetchNotificationData = async () => {
    try {
      const response = await axiosClient.get('/api/notificationdetail')
      if (response.status === 200) {
        setNotificationData(response.data)
      } else {
        setNotificationData([]);
      }
    } catch (error) {
      console.error("Error fetching noification details:", error);
      setNotificationData([]);
    }
  };

  const toggleNotification = async () => {
    setNotificationDisplay(prev => {
      const newDisplay = !prev; // Toggle the state
      if (newDisplay) {
        fetchNotificationLiveData(); // Call only if the new state is true
      }
      console.log(`[${getCurrentISTTime()}] newDisplay:`, newDisplay)
      return newDisplay; // Return the new state
    });
    await fetchNotificationData(); // Fetch notification data regardless of the display state
  };

  const getNotificationStatus = (status) => {
    switch (status) {
      case 'processing':
        return <Box><CircularProgress color="inherit" /></Box>;
      case 'success':
        return <Box><TaskIcon color={"success"} /></Box>;
      default:
        return null;
    }
  };


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <>



        {/* <NotificationsIcon
  onClick={toggleNotification}
  sx={{
    position: "fixed",
    top: 16,
    left: 52,
    cursor: "pointer",
    zIndex: 1500, // Ensure it's above the backdrop
  }}
/> */}


        {/* Drawer that appears on top of the main content */}

        <div  >
          {/*
            Outside Suspense, so it catches a chunk that fails to arrive as well as one
            that throws once it has. See ChunkErrorBoundary: a deploy changes every chunk
            hash, and a tab opened before it would otherwise go white.
          */}
          <ChunkErrorBoundary>
          <Suspense fallback={<Loader text="Dashboard is loading ..." />}>
            <Routes>

              {/* Public Routes */}

              {/* <Route path="/" element={<PreventAuthAccess element={<SignIn />} />} /> */}
              <Route path="/signin" element={<PreventAuthAccess element={<SignIn />} />} />
              <Route path="/signup" element={<SignUpRedirect />} />

              {/* <Route path="/approve/:token" element={<Approve/>} /> */}
              {/* Protected Routes */}

              <Route path="/" element={<ProtectedRoute element={<Dashboard />} />}>
                <Route index element={<EntryPoint />} />
                <Route path="dashbord" element={<DashboardCards />} />

                {/* Email Marketing Module */}
                <Route path="email-marketing" element={<EmailMarketingLayout />}>
                  <Route index element={<EmailDashboard />} />
                  <Route path="campaigns" element={<CampaignsPage />} />
                  <Route path="campaigns/:campaignId" element={<CampaignProfilePage />} />
                  <Route path="templates" element={<TemplatesPage />} />
                  <Route path="subscribers" element={<SubscribersPage />} />

                  {/* Email Verification Nested Module */}
                  <Route path="verification" element={<VerificationLayout />}>
                    <Route index element={<VerifyPage />} />
                    <Route path="batch" element={<BatchPage />} />
                    <Route path="batch/:id" element={<BatchDetailPage />} />
                    <Route path="history" element={<HistoryPage />} />
                    <Route path="analytics" element={<AnalyticsPage />} />
                  </Route>
                </Route>

                {/* WhatsApp CRM Module */}
                <Route path="whatsapp-marketing" element={<WhatsAppLayout />}>
                  <Route index element={<WhatsAppDashboard />} />
                  <Route path="inbox" element={<WhatsAppLiveInbox />} />
                  <Route path="campaigns" element={<WhatsAppCampaigns />} />
                  <Route path="templates" element={<WhatsAppTemplates />} />
                  <Route path="contacts" element={<WhatsAppContacts />} />
                  {/* Records were folded into the Campaigns/Templates/Contacts tables; keep old links working. */}
                  <Route path="records" element={<Navigate to="/whatsapp-marketing/campaigns" replace />} />
                </Route>

                {/* Sales Command Center Module */}
                <Route path="sales-command-center" element={<SalesCommandCenterLayout />}>
                  {/* The index is the dashboard, the run, or the live write-up —
                      whichever is true. There is no separate call route. */}
                  <Route index element={<SCCHome />} />
                  <Route path="pipeline" element={<SCCPipelineBoard />} />
                  <Route path="queue" element={<SCCCallingQueue />} />
                  <Route path="review" element={<SCCDayReview />} />
                  <Route path="history" element={<SCCCallHistory />} />
                  <Route path="call" element={<Navigate to="/sales-command-center" replace />} />
                  <Route path="intelligence" element={<SCCIntelligence />} />
                  <Route path="team" element={<SCCTeamAccess />} />
                </Route>

                <Route path="contact" element={<Contact />} />
                <Route path="account" element={<Account />} />
                <Route path="orders" element={<Order />} />
                <Route path="/aboutme" element={<UserInformation />} />
                <Route path="kanban" element={<Kanban />} />
                <Route path="ListAllTaskDiscussion" element={<TaskDisscutionManager />} />

                {/* <Route path="view-task/:userId" element={<ViewTask/>} /> */}
                {/* <Route path="meeting" element={<Meeting />} /> */}
                <Route path="help" element={<HelpTicketUI />} />
                <Route path="about" element={<AboutMee />} />
                <Route path="aboutus" element={<Aboutus />} />
                <Route path="aboutus/training" element={<TrainingCourseList />} />
                <Route path="aboutus/training/:courseId" element={<TrainingCourseDetail />} />
                <Route path="company/:id" element={<CompanyDetails />} />
                <Route path="targetcompany/:id" element={<TargetCompanyDetails />} />
                <Route path="CompanyDetails" element={<CompanyDetails />} />
                <Route path="database/:port" element={<DatabasePage />} />
                <Route path="entrypoint" element={<EntryPoint />} />
                <Route path="CurrentCustomer" element={<CurrentCustomerPage />} />
                <Route path="TargetCustomer" element={<TargetCustomer />} />
                <Route path="NotInterested" element={<NotInterested />} />
                <Route path="Compitator" element={<Compitator />} />
                {/* <Route path= "fileuploder" element = {<FileUploader/>}/> */}
                {/* <Route path="VendorCompetitor" element={<VendorCompetitor />} /> */}
                <Route path="VendorCompetitor" element={<VendorCompetitor />} />
                <Route path="statistics" element={<SuperAdminRoute element={<Statistics />} />} />
                <Route path="statistics/:sectionId" element={<SuperAdminRoute element={<StatisticsDetail />} />} />
                <Route path="/inquiry-received/:rfq_no" element={< Inquiryhandler />} />
                <Route path="DeletedCompanyDetails" element={<DeletedCompanyDetails />} />
                <Route path="DeletedCompanyDetails/:companyId" element={<DeletedCompanyDetails />} />
                <Route path="QuotationHeader" element={<QuotationHeader />} />
                <Route path="QuotationForm" element={<QuotationForm />} />
                <Route path="TargetCompanyDetails" element={<TargetCompanyDetails />} />
                <Route path="PurchasePage" element={<PurchasePage />} />
                <Route path="PurchaseID" element={<PurchaseID />} />
                <Route path="VendorDatabase" element={<VendorDatabase />} />
                <Route path="DeletedVendor" element={<DeletedVendor />} />
                <Route path="vendor/:id" element={<VendorDetails />} />
                <Route path="AddVendorSpecs" element={<AddVendorSpecs />} />
                <Route path="InquiryMatchSpecs" element={<InquiryMatchSpecs />} />
                <Route path="CustomizedHook" element={<CustomizedHook />} />
                <Route path="CardComponent" element={<CardComponent />} />
                <Route path="MaterialCode" element={<MaterialCode />} />
                <Route path="TaskManager" element={<TaskManager />} />
                <Route path="maintask" element={<Cleanhelp />} />
                <Route path="maintask/:activeTab/:taskId" element={<Cleanhelp />} />
                <Route path="kanban" element={<Kanban />} />
                <Route path="LoginPage" element={<LoginPage />} />
                <Route path="MaterialCodeGenerator" element={<MaterialCodeGenerator />} />
                <Route path="material/:material_code" element={<MaterialCodeTable />} />
                <Route path="Leads" element={<Header />} />
                <Route path="Estimations" element={<EstimationHeader />} />
                <Route path="FollowUp" element={<FollowUp />} />
                <Route path="TripPlanner" element={<TripPlannerDashboard />} />
                <Route path="TripPlanner/Settings" element={<TripPlannerSettings />} />
                <Route path="TripPlanner/:country" element={<CountryPlanner />} />
                <Route path="ColdCalling" element={<ColdCalling />} />
                <Route path="chatapp" element={<ChatApp />} />
                <Route path="/estimation-form/:rfq_no" element={<ProtectedRoute element={<EstimationForm />} />} />
                <Route path="/estimation-form/:rfq_no/:token" element={<ProtectedRoute element={<EstimationForm />} />} />
                <Route path="/quotation-form/:rfq_no" element={<ProtectedRoute element={<QuotationForm />} />} />
                <Route path='/smallchat' element={<Smallmain />} />
                {/* <Route path='/todo' element={<TaskScheduler />} /> */}
                <Route path='/todo' element={<TestTaskScheduler />} />
                <Route path="/helpticket" element={<HelpTicketUI />} />
                <Route path='/pdf' element={<QuotationPDF />} />
                <Route path='/cleantask' element={<Cleanhelp />} />

                <Route path="documents" element={<DocumentManagement />} />
                <Route path="client-registration" element={<ClientTable />} />
                <Route path="FollowUpBuying" element={<FollowUp />} />
                <Route path="FollowUpBiddingBudgetary" element={<FollowUpBiddingBuying />} />
                <Route path="FollowUpOrder" element={<FollowupOrder />} />
                <Route path="ColdCalling" element={<ColdCalling />} />
                <Route path="CurrentCustomerColdCalling" element={<CurrentCustomerColdCalling />} />
                <Route path="TargetCustomerColdCalling" element={<TargetCustomerColdCalling />} />
                <Route path="AI_test" element={<AIPromotedTest />} />
                <Route path="Promote" element={<Promote />} />
                <Route path="chatapp" element={<ChatApp />} />
                <Route path='/smallchat' element={<Smallmain />} />
                {/* domain */}
                <Route path='/domain-expiry-page' element={<Domain />} />
                <Route path="sol-office-database" element={<SolDatabase />} />
                {/* <Route path ='/test'   element = {<QuotationPDF/>}/>  */}


                <Route path="/ListUser" element={<UserList />} />
                {/* <Route path="/vendor/:id" element={<VendorDetails />} /> */}
                {/* // <Route path="vendor/:id" element={<VendorDetails />} />  */}
                <Route path='NotInterested' element={<NotInterested />} />
                <Route path="CurrentCustomerColdCalling" element={<CurrentCustomerColdCalling />} />
                <Route path="TargetCustomerColdCalling" element={<TargetCustomerColdCalling />} />
                {/* Logistics */}
                <Route path="vendors/air" element={<AirVendors />} />
                <Route path="vendors/sea" element={<SeaVendors />} />
                <Route path="vendors/road" element={<RoadVendors />} />

                <Route path="hr/employees" element={<EmployeeDirectory />} />
                <Route path="hr/employees/:id" element={<EmployeeProfile />} />
                <Route path="hr/designations" element={<DesignationList />} />
                <Route path="hr/designations/:designationId" element={<DesignationPage />} />
                {/* Attendance & Leave */}
                <Route path="hr/attendance" element={<AttendanceDashboard />} />
                <Route path="hr/leave" element={<LeaveManagement />} />

                {/* Payroll Management */}
                <Route path='hr/dashboard' element={<HrDashboard />} />
                <Route path="hr/payroll-dashboard" element={<PayrollDashboard />} />
                <Route path="hr/employees" element={<EmployeeDirectory />} />
                <Route path="hr/designations" element={<DesignationList />} />
                <  Route path="/hr/allowances" element={<AllowanceView />} />
                <Route path="hr/payslips" element={<PayslipList />} />
                <Route path="hr/ai-automation" element={<AIAutomationDashboard />} />

                <Route path="hr/attendance" element={<AttendanceDashboard />} />
                <Route path="hr/leave" element={<LeaveManagement />} />

                <Route path="hr/allowances" element={<AllowanceList />} />
                <Route path="hr/deductions" element={<DeductionList />} />
                <Route path="hr/loans" element={<LoanList />} />
                <Route path="hr/reimbursements" element={<ReimbursementList />} />
                {/* Recruitment */}
                <Route path="hr/recruitment/dashboard" element={<RecruitmentDashboard />} />
                <Route path="hr/recruitment/job-postings" element={<JobPostingList />} />
                <Route path="hr/recruitment/candidates" element={<CandidateList />} />
                <Route path="hr/recruitment/pipeline" element={<PipelineList />} />
                <Route path="hr/recruitment/interviews" element={<InterviewList />} />
                <Route path="hr/recruitment/call-details" element={<CallDetails />} />
                <Route path="hr/recruitment/call-report" element={<CallReport />} />
                <Route path="/recruitment/candidates/:candidateId" element={<CandidateView />} />

                <Route path="/recruitment/candidates/job/:jobId" element={<CandidateManagement />} />
                <Route path="/recruitment/interviews/scheduler" element={<InterviewScheduler />} />
                <Route path="/recruitment/interviews" element={<InterviewList />} />
                <Route path="/recruitment/interviews/:id" element={<InterviewList />} />


              </Route>
              {/* Routes that should not be nested under /dashboard */}


              {/* <Route path="/api/approve/:token" element={<Approve />} /> */}
              <Route path="/approve/:token" element={<Approve />} />
              {/* <Route path = '/CompletedTaskReview/:taskid' element={<CompletedTaskReview/>}/> */}
              {/* <Route path="/Estimations/:token" element={<EstimationHeader />} /> */}
              <Route path="/api/reset-password/approve/:token" element={<ResetApproval />} />
              {/* <Route path="/estimation-form/:rfq_no" element={<ProtectedRoute element={<EstimationForm />} />} />
              <Route path="/estimation-form/:rfq_no/:token" element={<ProtectedRoute element={<EstimationForm />} />} />
              <Route path="/quotation-form/:rfq_no" element={<ProtectedRoute element={<QuotationForm />} />} /> */}
              {/* <Route path="/inquiry-received/:rfq_no" element={<ProtectedRoute element={< Inquiryhandler />} />} /> */}
              <Route path="/InquiryForm/:token" element={<EmailEstimationForm />} />
              <Route path="/Test" element={<EmailEstimationFormOthers />} />

              {/* <Route path="hr" element={<HRMain />} />  */}
              {/* Employee Management */}


              {/* Admin Routes */}
              <Route path="/admin" element={<AdminRoute element={<Dashboard />} />}>
                {/* Admin-specific routes */}
              </Route>
              <Route path="notfound" element={<NotFound />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ChunkErrorBoundary>
        </div>
      </>
      {/* </Router> */}
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />


    </ThemeProvider>
  );
};
export default App;
